import Vue from 'vue'
import VueRouter from 'vue-router'
import AppLayout from '@/views/layout'
import { getMenuTree } from '@/api/system/menu'
import { getToken } from '@/utils/auth'
import store from '@/store'

Vue.use(VueRouter)

// 静态路由
export const constantRoutes = [
   {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/auth/login'),
      meta: { 
        requiresAuth: false,
        hideLayout: true  // 添加这个标记
      }
  },
  {
    path: '/',
    redirect: '/home',  // 关键修改：根路径重定向到/home
    meta: { hidden: true }
  },
  {
    path: '/home',
    name: 'Home',
    component: () => import('@/views/home'),
    meta: { 
      title: '系统首页', 
      tab: true,
      affix: true,  // 固定Tab
      icon: 'el-icon-s-home'
    }
  },
  {
      path: '/system/dict',
      component: () => import('@/views/dict/TypeList'),
      meta: {
        title: '字典管理',
        keepAlive: false
      }
  },
  {
    path: '/dashboard',
    component: () => import('@/views/dashboard'),
    meta: {
      title: '控制面板',
      tab: true,
      icon: 'el-icon-s-data'
    }
  },
  
  {
    path: '/404',
    component: () => import('@/views/system/NotFound'),
    hidden: true
  },
 
]

// 动态路由生成（核心方法）
function generateRoutes(menus, parentPath = '') {
  return menus
    .filter(menu => menu.visible !== 0)
    .map(menu => {
      const fullPath = parentPath + menu.path 
      const route = {
        path: fullPath,
        //name:menu.code,
        component: resolveComponent(menu),
        meta: {
          title: menu.name,
          icon: menu.icon,
          menuId: menu.id,
          pageCode: menu.page,
          params: JSON.parse(menu.params)
        }
      }

      if (menu.children?.length) {
        route.children = generateRoutes(menu.children, fullPath.endsWith('/') ? fullPath : fullPath + '/')
      }

      return route
    })
}

// 组件加载器（带错误处理）
function resolveComponent(menu) {
  // 目录类型使用布局组件
  if (menu.type === 1) return AppLayout
  
  // 页面组件动态导入
  const componentPath = `@/views/${menu.component}`
  return () => import(/* webpackChunkName: "dynamic-[request]" */ `@/views/${menu.component}`)
    .catch(err => {
      console.error(`[路由] 组件加载失败: ${componentPath}`, err)
      return import('@/views/error/fallback') // 确保此文件存在
    })
}

// 路由实例
const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: constantRoutes
})

// 重置路由（保留静态路由）
function resetRouter() {
  const newRouter = new VueRouter({
    mode: 'history',
    routes: constantRoutes
  })
  router.matcher = newRouter.matcher
}

// 添加动态路由（主方法）
export async function addDynamicRoutes() {
  // if (!getToken()) return false

  try {
    //console.time('[路由] 动态加载')
    const { data } = await getMenuTree()
    const asyncRoutes = await generateRoutes(data.menus)

    await resetRouter()
    asyncRoutes.forEach(route => router.addRoute(route))
    router.addRoute({ path: '*', redirect: '/404' })

    //console.timeEnd('[路由] 动态加载')
    return true
  } catch (err) {
    console.error('[路由] 加载失败:', err)
    return false
  }
}

// 在 router/index.js 中添加全局错误处理
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      // 重新抛出非重复导航错误
      throw err
    }
    // 静默处理重复导航错误
    //console.log('[路由] 重复导航已忽略:', location)
  })
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      throw err
    }
    //console.log('[路由] 重复替换已忽略:', location)
  })
}

router.beforeEach(async (to, from, next) => {
  // 修复：使用全路径比较而不是路径比较
  //console.log("from.fullPath",from.fullPath)
  //console.log("to.fullPath",to.fullPath)
  if (to.fullPath === from.fullPath) {
    //console.log('[路由] 跳过相同路径导航')
    return false;
  }
  // 跳过相同路径
  if (to.path === from.path) return next(false)
  
  // 处理根路径重定向
  if (to.path === '/') {
    return next('/home')
  }

  // 检查动态路由是否已加载
  const hasDynamicRoutes = router.getRoutes().length > constantRoutes.length
  
  if (!hasDynamicRoutes && getToken()) {
    try {
      //console.log('[路由守卫] 开始加载动态路由...')
      const success = await addDynamicRoutes()
      
      if (success) {
        // 动态路由加载完成后，重新匹配当前路由
        const matched = router.resolve(to.path).route
        if (matched.matched.length > 0) {
          //console.log('[路由守卫] 动态路由加载成功，重新导航')
          return next(to.fullPath) // 重新导航到当前路径
        }
      }
    } catch (error) {
      console.error('[路由守卫] 动态路由加载失败:', error)
    }
  }

  // 正常处理Tab
  if (to.meta.tab !== false && !to.hidden && to.matched.length > 0) {
    //console.log("当前路由信息:", to)
    
    // 确保store模块已加载
    if (!store._modules.root._children.tab) {
      //console.error('tab模块未加载!')
      return next()
    }
    
    if (to.fullPath !== "") {
      store.dispatch('tab/addTab', {
        path: to.path,
        title: to.meta.title || to.name,
        fullPath: to.fullPath,
        meta: to.meta
      }).catch(() => {
        // 降级方案
        store.commit('tab/ADD_TAB', {
          path: to.path,
          title: to.meta.title || to.name,
          meta: to.meta
        })
      })
    }
  }
  
  next()
})
export default router